import { TextField } from "@clockwise/design-system";
import { fg_subtle } from "@clockwise/design-system/tokens";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import classNames from "classnames";
import { MagnifyingGlass } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { useBoolean } from "usehooks-ts";
import { useOrgId } from "../../../hooks/useOrgId";
import {
  CalendarProfile,
  useCalendarProfilesFromSearch,
} from "../../../web-app-calendar/multi-calendar-select/util/MultiCalendarSelect.util";
import { useKeyboardSelect } from "../ChatInput/ChatInputModal/hooks/useKeyboardSelect";
import { Collaborator } from "../hooks/useCollaborators";

const SearchIcon = () => (
  <MagnifyingGlass fill="none" color={fg_subtle} size={16} className="cw-left-[5px]" />
);

export const CollaboratorSearchSelect = ({
  onSelect,
  query,
  setQuery,
  searchLoading,
  calendarProfiles,
}: {
  onSelect: (profile: CalendarProfile) => void;
  query: string;
  setQuery: (query: string) => void;
  searchLoading: boolean;
  calendarProfiles: CalendarProfile[];
}) => {
  const { value: isFocused, setTrue: setFocus, setFalse: setBlur } = useBoolean(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { activeIndex, setActiveIndex } = useKeyboardSelect({
    active: query.length > 0 && isFocused,
    inputRef,
    onSelect: (index) => {
      onSelect(calendarProfiles[index]);
    },
    optionCount: calendarProfiles?.length,
  });

  useEffect(() => {
    // Reset index to top on search change
    setActiveIndex(0);
  }, [query, setActiveIndex]);

  const noResults = calendarProfiles.length === 0 && !searchLoading;

  const onBlurDelay = () => {
    setTimeout(() => {
      setBlur();
    }, 200);
  };

  return (
    <div className="cw-relative">
      <TextField
        ref={inputRef}
        fullWidth
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
        startIcon={SearchIcon}
        placeholder="Search"
        onFocus={setFocus}
        onBlur={onBlurDelay}
      />
      {isFocused && (
        <div className="cw-absolute cw-w-full cw-bg-default cw-z-10 cw-mt-1 cw-border cw-border-solid cw-border-muted cw-rounded-lg cw-shadow-selectPopup">
          {noResults ? (
            <div className="cw-flex cw-group cw-justify-between cw-items-center cw-rounded-lg cw-p-1 cw-m-1">
              <div className="cw-flex cw-gap-3 cw-items-center cw-truncate">
                <div className="cw-body-sm cw-font-medium">No results</div>
              </div>
            </div>
          ) : (
            calendarProfiles.map((person, index) => {
              const { profile, primaryCalendar } = person;
              const fullName =
                profile?.givenName || profile?.familyName
                  ? `${profile?.givenName} ${profile?.familyName}`
                  : "";
              return (
                <div
                  key={primaryCalendar}
                  onClick={() => {
                    onSelect(person);
                  }}
                  className={classNames(
                    "cw-flex cw-group cw-justify-between cw-items-center cw-rounded-lg cw-p-1 cw-m-1 cw-cursor-pointer hover:cw-bg-neutral-inset",
                    { "cw-bg-neutral-inset": index === activeIndex },
                  )}
                >
                  <div className="cw-flex cw-gap-3 cw-items-center cw-truncate">
                    <AttendeeAvatar
                      calendarId={primaryCalendar}
                      profile={profile || undefined}
                      size="large"
                    />
                    <div className="cw-body-sm cw-font-medium">
                      <div>{fullName || primaryCalendar}</div>
                      {fullName && (
                        <div className="cw-text-11 cw-text-muted cw-font-normal">
                          {primaryCalendar}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export const CollaboratorSearch = ({
  currentSelectedCalIds,
  remainingTopCollaborators,
  onSelect,
}: {
  currentSelectedCalIds: string[];
  remainingTopCollaborators: Collaborator[];
  onSelect: (profile: CalendarProfile) => void;
}) => {
  const { orgId } = useOrgId();
  const [query, setQuery] = useState("");
  const { calendarProfiles, loading: searchLoading } = useCalendarProfilesFromSearch(
    orgId || "",
    query,
    currentSelectedCalIds,
  );

  const onClearAndSelect = (profile: CalendarProfile) => {
    if (!profile?.primaryCalendar) return;
    setQuery("");
    onSelect(profile);
  };

  const resultsForEmptyState = remainingTopCollaborators.map((collab) => {
    // When the search query is empty, we want to show the relevant top collaborators instead of just random users.
    // We've already fetched the top collaborators in the SidebarQuickFeed component, so we can use that data here.
    return {
      primaryCalendar: collab.calendarId,
      profile: {
        givenName: collab?.fullName || null,
        familyName: "",
        externalImageUrl: collab?.externalImageUrl || null,
      },
      isYou: false,
    };
  });

  return (
    <CollaboratorSearchSelect
      onSelect={onClearAndSelect}
      query={query}
      setQuery={setQuery}
      searchLoading={searchLoading}
      calendarProfiles={
        query || !resultsForEmptyState.length ? calendarProfiles : resultsForEmptyState
      }
    />
  );
};
